// Attachment reference shaping helpers (design: Attachment reference (embedded); Req 2.1, 2.5, 2.3)

// Attachment guardrails: at most MAX_ATTACHMENTS files totalling at most
// MAX_TOTAL_BYTES across a single ticket/reply submission.
export const MAX_ATTACHMENTS = 5
export const MAX_TOTAL_BYTES = 50 * 1024 * 1024 // 50 MB

// Given the already-attached items and the incoming files, returns which
// incoming files are acceptable and a reason for any rejection. Enforces both
// the count cap and the cumulative-size cap. `existing` and `incoming` items
// each expose a numeric `size` (bytes); existing attachment refs may omit size,
// treated as 0 for the running total.
export const checkAttachmentLimits = (existing, incoming, {
  maxCount = MAX_ATTACHMENTS,
  maxTotalBytes = MAX_TOTAL_BYTES
} = {}) => {
  const existingList = Array.isArray(existing) ? existing : []
  const incomingList = Array.isArray(incoming) ? incoming : []

  const accepted = []
  const rejected = []

  let count = existingList.length
  let total = existingList.reduce((sum, a) => sum + (a.size || 0), 0)

  for (const file of incomingList) {
    const size = file.size || 0
    if (count >= maxCount) {
      rejected.push({ file, reason: 'count' })
      continue
    }
    if (total + size > maxTotalBytes) {
      rejected.push({ file, reason: 'size' })
      continue
    }
    accepted.push(file)
    count += 1
    total += size
  }

  return { accepted, rejected }
}

// Produce a well-formed attachment reference with non-empty storagePath,
// fileName, and contentType (Req 2.1, 2.5). Carries an optional byte size so
// cumulative-size limits can be enforced across a submission.
export const toAttachmentRef = ({ storagePath, fileName, contentType, size }) => ({
  storagePath,
  fileName,
  contentType,
  ...(typeof size === 'number' ? { size } : {})
})

// Decide whether an attachment should render an image preview (Req 2.3)
export const isImage = (contentType) =>
  typeof contentType === 'string' && contentType.startsWith('image/')
